<script setup>
import {
  watch,
  reactive,
  inject,
  computed,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import phewIcon from '@/assets/images/phew-icon.svg';
import builtAndTrusted from '@/assets/images/built-and-trusted.svg';
import cardAlert from '@/components/cardAlert.vue';
import { posthogCapture } from "@/utils/posthog";
import { HIBB_USER_INPUT_EMAIL, HIBB_USER_INPUT_PHONE, HIBB_USER_INPUT_SUBMIT } from "@/constants/posthogEvents";
import { getUTMParams } from '@/utils';

import { useMainStore } from '../../store.js';

const main = useMainStore();

const route = useRoute();
const router = useRouter();

const cryoptojs = inject('cryptojs');
const cioanalytics = inject('cioanalytics')

const state = reactive({
  search: '',
  hashedValue: '',
  searchActive: false,
  searchOption: 'email',
  emailError: false,
  phoneError: false,
  show: false,
  submitActive: false,
  showRecentSearches: false,
  mostRecent: {
    email: {
      status: false,
      placeholder: null,
    },
    phone: {
      status: false,
      placeholder: null,
    }
  },
  recentSearches: {
    email: [],
    phone: [],
  }
});

function closeRecentSearches() {
  state.showRecentSearches = false;
}

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const validateNumber = (input) => {
    // Remove all non-digit characters
    const cleanedInput = String(input).replace(/\D/g, '');
    
    // USA phone numbers should be exactly 10 digits (without country code)
    // Or 11 digits if they include the country code (1)
    if (cleanedInput.length === 10) {
      // Check if it's a valid USA number format
      // First digit should be 2-9 (area codes don't start with 0 or 1)
      // Fourth digit should be 2-9 (exchange codes don't start with 0 or 1)
      return /^[2-9]\d{2}[2-9]\d{6}$/.test(cleanedInput);
    } else if (cleanedInput.length === 11 && cleanedInput.startsWith('1')) {
      // If 11 digits, should start with 1 (USA country code)
      const withoutCountryCode = cleanedInput.substring(1);
      return /^[2-9]\d{2}[2-9]\d{6}$/.test(withoutCountryCode);
    }
    
    return false;
};

const notVisible = computed(() => {
  if (route.fullPath === '/terms' || route.fullPath === '/privacy') {
    return false;
  } else {
    return true;
  }
});

const showRecentSearches = () => {
  getRecentSearches();
  if (
    state.recentSearches.email.length > 0 ||
    state.recentSearches.phone.length > 0
  ) {
    state.showRecentSearches = true;
  }
};

const setRecentSearch = (search) => {
  state.search = search;
  state.mostRecent.email.placeholder = search;
};

const removeRecentSearch = (search) => {
  if (state.recentSearches.email.includes(search)) {
    state.recentSearches.email = state.recentSearches.email.filter(
      (item) => item !== search
    );
  }

  if (state.recentSearches.phone.includes(search)) {
    state.recentSearches.phone = state.recentSearches.phone.filter(
      (item) => item !== search
    );
  }
};

const getRecentSearches = () => {
  const storedSearches = localStorage.getItem('recentSearchesEmail');
  const searches = storedSearches ? JSON.parse(storedSearches) : [];
  console.log('from local', searches);
};


function fbqTrackingLead() {
  if (!window.fbq) return;
  window.fbq('track', 'Lead');
}
function fbqTracking() {
  if (!window.fbq) return;
  fbqTrackingLead();
  window.fbq('track', 'Search');
}
function customerioSubscribe(email) {
  if (!validateEmail(email)) return;

  try {
    const customerId = btoa(email);

    if (cioanalytics?.identify) {
      cioanalytics.identify({
        id: customerId,
        email: email,
        subscribed: true,
        source: 'hibb',
      });
    }
  } catch (err) {
    console.error('customerioSubscribe failed:', err);
  }
}

function searchSubmit() {
  if (state.searchOption === 'email') {
    if (validateEmail(state.search)) {
      fbqTracking();
      customerioSubscribe(state.search);
      posthogCapture(HIBB_USER_INPUT_SUBMIT, {'hibb_field' : 'email', email: state.search});
      router.push({
        name: 'Search',
        query: { ...route.query, find: state.hashedValue },
      });
      state.showRecentSearches = false;
      if (!state.recentSearches.email.includes(state.search)) {
        // If the string doesn't exist, add it to the array
        state.mostRecent.email.status = true;
        state.mostRecent.email.placeholder = state.search;
        state.recentSearches.email = [
          ...state.recentSearches.email,
          state.search,
        ];
      }
      localStorage.setItem(
        'recentSearchesEmail',
        JSON.stringify(state.recentSearches.email)
      );

      // console.log(
      //   'whats set to local storage',
      //   JSON.stringify(state.recentSearches.email)
      // );
      state.show = false;
      state.emailError = false;
      state.submitActive = false;
      state.search = '';
    } else {
      state.emailError = true;
    }
  }

  if (state.searchOption === 'phone') {
    if (validateNumber(state.search)) {
      fbqTracking();
      // Clean the phone number (remove all non-digits)
      const cleanedPhone = state.search.replace(/\D/g, '');
      
      // Ensure it's 10 digits (remove country code if present)
      const phoneNumber = cleanedPhone.length === 11 && cleanedPhone.startsWith('1') 
        ? cleanedPhone.substring(1) 
        : cleanedPhone;
      
      state.showRecentSearches = false;
      if (!state.recentSearches.phone.includes(state.search)) {
        // If the string doesn't exist, add it to the array
        state.mostRecent.phone.status = true;
        state.mostRecent.phone.placeholder = state.search;
        state.recentSearches.phone = [
          ...state.recentSearches.phone,
          state.search,
        ];
      }
      state.show = false;
      state.phoneError = false;
      state.submitActive = false;
      state.search = '';


      // Construct the URL
      const redirectUrl = `https://my.cloaked.com/data-delete?phone=${phoneNumber}&${getUTMParams()}`;
      
      // Open in new tab
      posthogCapture(HIBB_USER_INPUT_SUBMIT, {'hibb_field' : 'phone' });
      window.open(redirectUrl, '_blank');
    } else {
      state.phoneError = true;
    }
  }
}

function searchOptionChange(option) {
  if(state.searchOption === option) return;

  state.searchOption = option;
  if(option === 'email'){
    posthogCapture(HIBB_USER_INPUT_EMAIL);
  } else {
    posthogCapture(HIBB_USER_INPUT_PHONE);
  }

  state.search = ''
  state.emailError = false;
  state.phoneError = false;
}

watch(
  () => route.query.find,
  () => {
    if (route.query.find || route.query.find !== '') {
      state.searchActive = true;
    }

    if (!route.query.find || route.query.find === '') {
      state.searchActive = false;
      document.body.classList.remove(
        'danger',
        'alert',
        'nice',
        'good'
      );
    }
  }
);

watch(
  () => state.search,
  () => {
    if (state.search !== '') {
      if (state.searchOption === 'email') {
        state.hashedValue = cryoptojs.AES.encrypt(
          state.search,
          '1'
        ).toString();
      } else if (state.searchOption === 'phone') {
        state.hashedValue = cryoptojs.AES.encrypt(
          '+1' + state.search,
          '1'
        ).toString();
      }
    }

    if (state.search.length > 0) {
      state.submitActive = true;
    } else {
      state.submitActive = false;
    }
  }
);

function homePage() {
  router.push('/');
  state.show = false;
  state.search = '';
  main.setBreachCount(null);
}

function closeButton() {
  state.searchActive = false;
  state.search = '';
  state.mostRecent.status = false;
}

function goodNewsBanner() {
  if (state.searchOption === 'email') {
    return 'email address';
  } else {
    return 'phone number';
  }
}
const showRedirectNotice = computed(() => {
  return state.searchOption === 'phone' && state.search.length > 0;
});

</script>

<template>
  <header
    :class="{
      search: state.searchActive,
      phew: main.breachCount === 0,
    }"
  >
    <div class="container">
      <div class="logo">
        <div
          class="icon"
          @click="homePage"
        >
          <div
            v-if="main.breachCount > 0 || main.breachCount === null"
          >
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <path
                  d="M39.2554 0.83048C39.6528 0.387208 40.3471 0.387207 40.7446 0.83048L43.1594 3.52392C43.5021 3.90622 44.0787 3.96682 44.4935 3.66414L47.4155 1.53162C47.8964 1.18066 48.5755 1.32502 48.8721 1.84123L50.6741 4.97788C50.9299 5.42309 51.4813 5.60224 51.9499 5.39241L55.2515 3.91402C55.7948 3.67071 56.4291 3.95311 56.6119 4.5197L57.7224 7.96248C57.88 8.45114 58.3821 8.74102 58.8841 8.63319L62.4209 7.87355C63.003 7.74853 63.5647 8.15663 63.6256 8.74884L63.9961 12.3473C64.0487 12.858 64.4795 13.246 64.993 13.2449L68.6104 13.2372C69.2057 13.2359 69.6703 13.7518 69.6068 14.3438L69.221 17.9406C69.1663 18.4511 69.5071 18.9202 70.0095 19.0259L73.5495 19.7704C74.1321 19.893 74.4793 20.4942 74.2941 21.0601L73.1689 24.4981C73.0092 24.986 73.245 25.5157 73.7145 25.7235L77.0223 27.1878C77.5667 27.4288 77.7813 28.0891 77.4825 28.6041L75.6671 31.733C75.4094 32.1771 75.53 32.7442 75.946 33.0451L78.8771 35.1652C79.3595 35.5141 79.4321 36.2046 79.0328 36.6462L76.6065 39.3293C76.2621 39.7101 76.2621 40.2899 76.6065 40.6707L79.0328 43.3538C79.4321 43.7954 79.3595 44.4859 78.8771 44.8348L75.946 46.9549C75.53 47.2558 75.4094 47.8229 75.6671 48.267L77.4825 51.3959C77.7813 51.9109 77.5667 52.5712 77.0223 52.8122L73.7145 54.2765C73.245 54.4843 73.0092 55.0139 73.1689 55.5019L74.2941 58.9399C74.4793 59.5057 74.1321 60.107 73.5495 60.2296L70.0095 60.9741C69.5071 61.0798 69.1663 61.5489 69.221 62.0594L69.6068 65.6562C69.6703 66.2481 69.2057 66.7641 68.6104 66.7628L64.993 66.7551C64.4795 66.754 64.0487 67.142 63.9961 67.6527L63.6256 71.2512C63.5647 71.8434 63.003 72.2515 62.4209 72.1264L58.8841 71.3668C58.3821 71.259 57.88 71.5489 57.7224 72.0375L56.6119 75.4803C56.4291 76.0469 55.7948 76.3293 55.2515 76.086L51.9499 74.6076C51.4813 74.3977 50.9299 74.5769 50.6741 75.0221L48.8721 78.1588C48.5755 78.675 47.8964 78.8193 47.4155 78.4684L44.4935 76.3359C44.0787 76.0332 43.5021 76.0938 43.1594 76.4761L40.7446 79.1695C40.3471 79.6128 39.6528 79.6128 39.2554 79.1695L36.8406 76.4761C36.4979 76.0938 35.9213 76.0332 35.5065 76.3359L32.5845 78.4684C32.1036 78.8193 31.4245 78.675 31.1279 78.1588L29.3258 75.0221C29.0701 74.5769 28.5187 74.3977 28.0501 74.6076L24.7485 76.086C24.2052 76.3293 23.5709 76.0469 23.3881 75.4803L22.2776 72.0375C22.12 71.5489 21.6179 71.259 21.1159 71.3668L17.5791 72.1264C16.997 72.2515 16.4353 71.8434 16.3744 71.2512L16.0039 67.6527C15.9513 67.142 15.5205 66.754 15.007 66.7551L11.3896 66.7628C10.7942 66.7641 10.3297 66.2481 10.3932 65.6562L10.7789 62.0594C10.8337 61.5489 10.4929 61.0798 9.99048 60.9741L6.45048 60.2296C5.86789 60.107 5.52074 59.5057 5.70591 58.9399L6.8311 55.5019C6.9908 55.0139 6.75499 54.4843 6.28549 54.2765L2.97766 52.8122C2.43327 52.5712 2.21872 51.9109 2.51749 51.3959L4.33288 48.267C4.59056 47.8229 4.47002 47.2558 4.05399 46.9549L1.12288 44.8348C0.640497 44.4859 0.567922 43.7954 0.967225 43.3538L3.39349 40.6707C3.73787 40.2899 3.73787 39.7101 3.39349 39.3293L0.967225 36.6462C0.567923 36.2046 0.640496 35.5141 1.12288 35.1652L4.05399 33.0451C4.47002 32.7442 4.59056 32.1771 4.33288 31.733L2.51749 28.6041C2.21872 28.0891 2.43327 27.4288 2.97766 27.1878L6.28549 25.7235C6.75499 25.5157 6.9908 24.986 6.8311 24.4981L5.70591 21.0601C5.52074 20.4942 5.86789 19.893 6.45048 19.7704L9.99048 19.0259C10.4929 18.9202 10.8337 18.4511 10.7789 17.9406L10.3932 14.3438C10.3297 13.7519 10.7942 13.2359 11.3896 13.2372L15.007 13.2449C15.5205 13.246 15.9513 12.858 16.0039 12.3473L16.3744 8.74884C16.4353 8.15663 16.997 7.74853 17.5791 7.87355L21.1159 8.63319C21.6179 8.74102 22.12 8.45114 22.2776 7.96248L23.3881 4.51971C23.5709 3.95311 24.2052 3.67071 24.7485 3.91402L28.0501 5.39241C28.5187 5.60224 29.0701 5.42309 29.3259 4.97788L31.1279 1.84123C31.4245 1.32502 32.1036 1.18066 32.5845 1.53162L35.5065 3.66414C35.9213 3.96682 36.4979 3.90622 36.8406 3.52392L39.2554 0.83048Z"
                  fill="currentColor"
                />
              </g>
            </svg>
            <span />
          </div>
          <div v-else>
            <phewIcon />
          </div>
        </div>

        <div
          class="name"
          @click="homePage"
        >
          <span>HaveIBeenBreached</span><sup>&trade;</sup>
          <div class="underline" />
        </div>
      </div>

      <div
        class="menu-button"
        :class="{ active: state.show }"
      >
        <a
          :href="`https://www.cloaked.com/?${getUTMParams()}`"
          target="_blank"
          class="external"
          @click="fbqTrackingLead"
        >
          <span>Cloaked</span>
          <span class="menu-button-icon">
            <svg
              v-if="main.breachCount !== 0"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.7188 8.91406C11.7188 9.21094 11.6198 9.46094 11.4219 9.66406C11.2292 9.86198 10.9974 9.96094 10.7266 9.96094C10.4505 9.96094 10.2188 9.86198 10.0312 9.66406C9.84896 9.46094 9.75781 9.21615 9.75781 8.92969V6.125L9.86719 3.60938L8.69531 4.91406L2.04688 11.5703C1.82292 11.7891 1.57552 11.8984 1.30469 11.8984C1.1224 11.8984 0.953125 11.849 0.796875 11.75C0.640625 11.651 0.513021 11.526 0.414062 11.375C0.320312 11.2188 0.273438 11.0521 0.273438 10.875C0.273438 10.6042 0.385417 10.3542 0.609375 10.125L7.25781 3.47656L8.5625 2.32031L5.96094 2.41406H3.24219C2.96094 2.41406 2.71875 2.32031 2.51562 2.13281C2.3125 1.94531 2.21094 1.71615 2.21094 1.44531C2.21094 1.17448 2.3099 0.942708 2.50781 0.75C2.70573 0.552083 2.95573 0.453125 3.25781 0.453125H10.6562C10.9792 0.453125 11.237 0.552083 11.4297 0.75C11.6276 0.942708 11.7266 1.19792 11.7266 1.51562L11.7188 8.91406Z"
                fill="black"
              />
            </svg>
            <svg
              v-else
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.7188 9.41406C11.7188 9.71094 11.6198 9.96094 11.4219 10.1641C11.2292 10.362 10.9974 10.4609 10.7266 10.4609C10.4505 10.4609 10.2188 10.362 10.0312 10.1641C9.84896 9.96094 9.75781 9.71615 9.75781 9.42969V6.625L9.86719 4.10938L8.69531 5.41406L2.04688 12.0703C1.82292 12.2891 1.57552 12.3984 1.30469 12.3984C1.1224 12.3984 0.953125 12.349 0.796875 12.25C0.640625 12.151 0.513021 12.026 0.414062 11.875C0.320312 11.7188 0.273438 11.5521 0.273438 11.375C0.273438 11.1042 0.385417 10.8542 0.609375 10.625L7.25781 3.97656L8.5625 2.82031L5.96094 2.91406H3.24219C2.96094 2.91406 2.71875 2.82031 2.51562 2.63281C2.3125 2.44531 2.21094 2.21615 2.21094 1.94531C2.21094 1.67448 2.3099 1.44271 2.50781 1.25C2.70573 1.05208 2.95573 0.953125 3.25781 0.953125H10.6562C10.9792 0.953125 11.237 1.05208 11.4297 1.25C11.6276 1.44271 11.7266 1.69792 11.7266 2.01562L11.7188 9.41406Z"
                fill="white"
              />
            </svg>
          </span>
        </a>
        <div class="underline" />
      </div>

      <nav>
        <ul>
          <!-- <li :class="{active: route.fullPath === '/commitment'}"><a @click="commitmentPage">Our Commitment</a></li> -->
          <li>
            <a
              :href="`https://www.cloaked.com/?${getUTMParams()}`"
              target="_blank"
              class="external"
            >
              <span>Cloaked</span>
              <span>
                <svg
                  v-if="main.breachCount !== 0"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.7188 8.91406C11.7188 9.21094 11.6198 9.46094 11.4219 9.66406C11.2292 9.86198 10.9974 9.96094 10.7266 9.96094C10.4505 9.96094 10.2188 9.86198 10.0312 9.66406C9.84896 9.46094 9.75781 9.21615 9.75781 8.92969V6.125L9.86719 3.60938L8.69531 4.91406L2.04688 11.5703C1.82292 11.7891 1.57552 11.8984 1.30469 11.8984C1.1224 11.8984 0.953125 11.849 0.796875 11.75C0.640625 11.651 0.513021 11.526 0.414062 11.375C0.320312 11.2188 0.273438 11.0521 0.273438 10.875C0.273438 10.6042 0.385417 10.3542 0.609375 10.125L7.25781 3.47656L8.5625 2.32031L5.96094 2.41406H3.24219C2.96094 2.41406 2.71875 2.32031 2.51562 2.13281C2.3125 1.94531 2.21094 1.71615 2.21094 1.44531C2.21094 1.17448 2.3099 0.942708 2.50781 0.75C2.70573 0.552083 2.95573 0.453125 3.25781 0.453125H10.6562C10.9792 0.453125 11.237 0.552083 11.4297 0.75C11.6276 0.942708 11.7266 1.19792 11.7266 1.51562L11.7188 8.91406Z"
                    fill="black"
                  />
                </svg>
                <svg
                  v-else
                  width="12"
                  height="13"
                  viewBox="0 0 12 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.7188 9.41406C11.7188 9.71094 11.6198 9.96094 11.4219 10.1641C11.2292 10.362 10.9974 10.4609 10.7266 10.4609C10.4505 10.4609 10.2188 10.362 10.0312 10.1641C9.84896 9.96094 9.75781 9.71615 9.75781 9.42969V6.625L9.86719 4.10938L8.69531 5.41406L2.04688 12.0703C1.82292 12.2891 1.57552 12.3984 1.30469 12.3984C1.1224 12.3984 0.953125 12.349 0.796875 12.25C0.640625 12.151 0.513021 12.026 0.414062 11.875C0.320312 11.7188 0.273438 11.5521 0.273438 11.375C0.273438 11.1042 0.385417 10.8542 0.609375 10.625L7.25781 3.97656L8.5625 2.82031L5.96094 2.91406H3.24219C2.96094 2.91406 2.71875 2.82031 2.51562 2.63281C2.3125 2.44531 2.21094 2.21615 2.21094 1.94531C2.21094 1.67448 2.3099 1.44271 2.50781 1.25C2.70573 1.05208 2.95573 0.953125 3.25781 0.953125H10.6562C10.9792 0.953125 11.237 1.05208 11.4297 1.25C11.6276 1.44271 11.7266 1.69792 11.7266 2.01562L11.7188 9.41406Z"
                    fill="white"
                  />
                </svg>
              </span>
            </a>
            <div class="underline" />
          </li>
        </ul>
      </nav>

      <div
        v-if="notVisible"
        class="content"
      >
        <div
          class="text"
          :class="{ active: route.path === '/', 'text-dark' : main.breachCount !== 0 }"
        >
          <h1 :class="{ 'text-content' : main.breachCount === 0 }">
            <b>Americans</b>
            lost
            <b>$12.5 Billion</b>
            to Identity Theft and Fraud in <b>2024.</b>
          </h1>
          It all starts with your email and phone number getting breached
        </div>

        <div
          class="search-wrapper"
          :class="{ resultsview: route.path !== '/' }"
        >
          <div class="buttons-mobile">
            <button
              :class="{ active: state.searchOption === 'email' }"
              @click="searchOptionChange('email')"
            >
              <svg
                width="29"
                height="24"
                viewBox="0 0 29 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.2837 13.2612C13.7739 13.2612 13.2466 12.9888 12.7017 12.4438L1.75928 1.59375C2.21631 1.18945 2.98535 0.987305 4.06641 0.987305H24.1846C25.4062 0.987305 26.2632 1.20264 26.7554 1.6333L15.8789 12.4438C15.3252 12.9888 14.7935 13.2612 14.2837 13.2612ZM0.52002 20.8813C0.291504 20.4771 0.177246 19.8179 0.177246 18.9038V5.11377C0.177246 4.17334 0.300293 3.49219 0.546387 3.07031L9.5376 11.9561L0.52002 20.8813ZM4.34326 23.0435C3.12158 23.0435 2.25146 22.8237 1.73291 22.3843L10.8955 13.2876L11.647 14.0391C12.4995 14.8564 13.3784 15.2651 14.2837 15.2651C15.1802 15.2651 16.0547 14.8564 16.9072 14.0391L17.6587 13.2876L26.8477 22.3975C26.373 22.8281 25.582 23.0435 24.4746 23.0435H4.34326ZM28.021 20.8682L19.0298 11.9561L27.9946 3.10986C28.2319 3.52295 28.3506 4.19092 28.3506 5.11377V18.9038C28.3506 19.8091 28.2407 20.4639 28.021 20.8682Z"
                  fill="currentColor"
                />
              </svg>
              Emails
            </button>
            <button
              :class="{ active: state.searchOption === 'phone' }"
              @click="searchOptionChange('phone')"
            >
              <svg
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.3999 17.7437C6.16064 16.5132 5.04883 15.2212 4.06445 13.8677C3.08008 12.5054 2.30225 11.1475 1.73096 9.79395C1.15967 8.43164 0.874023 7.13525 0.874023 5.90479C0.874023 5.09619 1.01465 4.33154 1.2959 3.61084C1.58594 2.89014 2.04297 2.23535 2.66699 1.64648C3.42285 0.925781 4.24023 0.56543 5.11914 0.56543C5.47949 0.56543 5.81787 0.640137 6.13428 0.789551C6.45947 0.938965 6.73633 1.17627 6.96484 1.50146L9.68066 5.29834C9.8916 5.59717 10.0498 5.87842 10.1553 6.14209C10.2607 6.40576 10.3135 6.64746 10.3135 6.86719C10.3135 7.16602 10.23 7.45166 10.063 7.72412C9.90479 7.99658 9.68506 8.27783 9.40381 8.56787L8.50732 9.47754C8.37549 9.60938 8.30957 9.76758 8.30957 9.95215C8.30957 10.0576 8.32275 10.1543 8.34912 10.2422C8.38428 10.3301 8.41943 10.4092 8.45459 10.4795C8.66553 10.875 9.03467 11.3848 9.56201 12.0088C10.0894 12.624 10.665 13.2437 11.2891 13.8677C11.9131 14.4917 12.5327 15.0674 13.1479 15.5947C13.772 16.1133 14.2817 16.4824 14.6772 16.7021C14.7476 16.7373 14.8267 16.7725 14.9146 16.8076C15.0024 16.834 15.0991 16.8472 15.2046 16.8472C15.3979 16.8472 15.5562 16.7812 15.6792 16.6494L16.6021 15.7529C16.8833 15.4629 17.1602 15.2388 17.4326 15.0806C17.7051 14.9224 17.9863 14.8433 18.2764 14.8433C18.5137 14.8433 18.7598 14.896 19.0146 15.0015C19.2695 15.0981 19.5464 15.2563 19.8452 15.4761L23.6948 18.2051C24.02 18.4336 24.2529 18.7061 24.3936 19.0225C24.5342 19.3389 24.6045 19.6641 24.6045 19.998C24.6045 20.4287 24.5078 20.855 24.3145 21.2769C24.1211 21.6987 23.8574 22.0986 23.5234 22.4766C22.9434 23.1182 22.2974 23.584 21.5854 23.874C20.8735 24.1729 20.1045 24.3223 19.2783 24.3223C18.0566 24.3223 16.7646 24.0322 15.4023 23.4521C14.04 22.8809 12.6733 22.0942 11.3022 21.0923C9.93115 20.0991 8.63037 18.9829 7.3999 17.7437Z"
                  fill="currentColor"
                />
              </svg>
              Numbers
            </button>
          </div>
          <div
            class="search"
            :class="{
              active: state.searchActive,
              submit: state.submitActive,
              error: state.emailError || state.phoneError,
              recent: state.showRecentSearches,
            }"
            @click="state.searchActive = false"
          >
            <div
              class="search-inner"
              @click="state.searchActive = false"
            >
              <div
                v-on-click-outside="closeRecentSearches"
                class="input"
                :class="{
                  error: state.phoneError || state.emailError,
                  phone: state.searchOption === 'phone',
                }"
                @click="state.searchActive = false"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 17.035L7.28567 11.3207"
                    stroke="#191E23"
                    stroke-width="1.71428"
                  />
                  <ellipse
                    cx="11.2952"
                    cy="6.71493"
                    rx="5.70537"
                    ry="5.70541"
                    stroke="#191E23"
                    stroke-width="1.71428"
                  />
                </svg>

                <div
                  v-if="state.searchOption === 'phone'"
                  class="country"
                >
                  <div class="flag">
                    <img src="@/assets/images/flags/usa.png">
                  </div>

                  <div class="code">
                    <span>+1</span>
                  </div>
                </div>

                <input
                  v-if="state.searchOption === 'email'"
                  v-model="state.search"
                  autocomplete="off"
                  :disabled="state.searchActive"
                  type="email"
                  :placeholder="
                    state.mostRecent.email.status
                      ? state.mostRecent.email.placeholder
                      : 'Scan your personal email'
                  "
                  @keyup.enter="searchSubmit"
                  @focus="showRecentSearches()"
                >
                
              
                <input
                  v-if="state.searchOption === 'phone'"
                  v-model="state.search"
                  autocomplete="off"
                  :disabled="state.searchActive"
                  type="tel"
                  :placeholder="
                    state.mostRecent.phone.status
                      ? state.mostRecent.phone.placeholder
                      : 'Scan your personal number'
                  "
                  @keyup.enter="searchSubmit"
                  @focus="showRecentSearches()"
                >
                <div
                  v-if="
                    state.showRecentSearches &&
                      state.searchOption === 'phone'
                  "
                  class="recent-searches"
                >
                  <div
                    v-for="phone in state.recentSearches.phone"
                    :key="phone[i]"
                    @click="state.search = phone"
                  >
                    {{ phone }}
                  </div>
                </div>
                <div
                  v-if="
                    state.emailError ||
                      (state.phoneError && state.search !== '')
                  "
                  class="error"
                >
                  <p v-if="state.emailError">
                    Please enter a valid email address.
                  </p>
                  <p v-if="state.phoneError">
                    Please enter a valid phone number.
                  </p>
                </div>
              </div>

              <div class="buttons">
                <button
                  :class="{ active: state.searchOption === 'email' }"
                  @click="searchOptionChange('email')"
                >
                  <svg
                    width="29"
                    height="24"
                    viewBox="0 0 29 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.2837 13.2612C13.7739 13.2612 13.2466 12.9888 12.7017 12.4438L1.75928 1.59375C2.21631 1.18945 2.98535 0.987305 4.06641 0.987305H24.1846C25.4062 0.987305 26.2632 1.20264 26.7554 1.6333L15.8789 12.4438C15.3252 12.9888 14.7935 13.2612 14.2837 13.2612ZM0.52002 20.8813C0.291504 20.4771 0.177246 19.8179 0.177246 18.9038V5.11377C0.177246 4.17334 0.300293 3.49219 0.546387 3.07031L9.5376 11.9561L0.52002 20.8813ZM4.34326 23.0435C3.12158 23.0435 2.25146 22.8237 1.73291 22.3843L10.8955 13.2876L11.647 14.0391C12.4995 14.8564 13.3784 15.2651 14.2837 15.2651C15.1802 15.2651 16.0547 14.8564 16.9072 14.0391L17.6587 13.2876L26.8477 22.3975C26.373 22.8281 25.582 23.0435 24.4746 23.0435H4.34326ZM28.021 20.8682L19.0298 11.9561L27.9946 3.10986C28.2319 3.52295 28.3506 4.19092 28.3506 5.11377V18.9038C28.3506 19.8091 28.2407 20.4639 28.021 20.8682Z"
                      fill="currentColor"
                    />
                  </svg>
                  Emails
                </button>
                <button
                  :class="{ active: state.searchOption === 'phone' }"
                  @click="searchOptionChange('phone')"
                >
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.3999 17.7437C6.16064 16.5132 5.04883 15.2212 4.06445 13.8677C3.08008 12.5054 2.30225 11.1475 1.73096 9.79395C1.15967 8.43164 0.874023 7.13525 0.874023 5.90479C0.874023 5.09619 1.01465 4.33154 1.2959 3.61084C1.58594 2.89014 2.04297 2.23535 2.66699 1.64648C3.42285 0.925781 4.24023 0.56543 5.11914 0.56543C5.47949 0.56543 5.81787 0.640137 6.13428 0.789551C6.45947 0.938965 6.73633 1.17627 6.96484 1.50146L9.68066 5.29834C9.8916 5.59717 10.0498 5.87842 10.1553 6.14209C10.2607 6.40576 10.3135 6.64746 10.3135 6.86719C10.3135 7.16602 10.23 7.45166 10.063 7.72412C9.90479 7.99658 9.68506 8.27783 9.40381 8.56787L8.50732 9.47754C8.37549 9.60938 8.30957 9.76758 8.30957 9.95215C8.30957 10.0576 8.32275 10.1543 8.34912 10.2422C8.38428 10.3301 8.41943 10.4092 8.45459 10.4795C8.66553 10.875 9.03467 11.3848 9.56201 12.0088C10.0894 12.624 10.665 13.2437 11.2891 13.8677C11.9131 14.4917 12.5327 15.0674 13.1479 15.5947C13.772 16.1133 14.2817 16.4824 14.6772 16.7021C14.7476 16.7373 14.8267 16.7725 14.9146 16.8076C15.0024 16.834 15.0991 16.8472 15.2046 16.8472C15.3979 16.8472 15.5562 16.7812 15.6792 16.6494L16.6021 15.7529C16.8833 15.4629 17.1602 15.2388 17.4326 15.0806C17.7051 14.9224 17.9863 14.8433 18.2764 14.8433C18.5137 14.8433 18.7598 14.896 19.0146 15.0015C19.2695 15.0981 19.5464 15.2563 19.8452 15.4761L23.6948 18.2051C24.02 18.4336 24.2529 18.7061 24.3936 19.0225C24.5342 19.3389 24.6045 19.6641 24.6045 19.998C24.6045 20.4287 24.5078 20.855 24.3145 21.2769C24.1211 21.6987 23.8574 22.0986 23.5234 22.4766C22.9434 23.1182 22.2974 23.584 21.5854 23.874C20.8735 24.1729 20.1045 24.3223 19.2783 24.3223C18.0566 24.3223 16.7646 24.0322 15.4023 23.4521C14.04 22.8809 12.6733 22.0942 11.3022 21.0923C9.93115 20.0991 8.63037 18.9829 7.3999 17.7437Z"
                      fill="currentColor"
                    />
                  </svg>
                  Numbers
                </button>
              </div>

              <div
                class="submit"
                :class="{ submitSmall: showRedirectNotice }"
                @click="searchSubmit"
              >
                <svg
                  v-if="!showRedirectNotice"
                  width="16"
                  height="13"
                  viewBox="0 0 16 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.1797 6.64062C15.1797 6.9375 15.0729 7.1875 14.8594 7.39062L10.0312 12.2109C9.81771 12.4193 9.58333 12.5234 9.32812 12.5234C9.03125 12.5234 8.78906 12.4323 8.60156 12.25C8.41927 12.0677 8.32812 11.8411 8.32812 11.5703C8.32812 11.4193 8.35677 11.2839 8.41406 11.1641C8.47656 11.0391 8.55208 10.9323 8.64062 10.8438L10.2969 9.17969L12.8125 6.875L13.2266 7.5L10.4297 7.65625H1.92969C1.61719 7.65625 1.36458 7.5625 1.17188 7.375C0.979167 7.1875 0.882812 6.94271 0.882812 6.64062C0.882812 6.34375 0.979167 6.10417 1.17188 5.92188C1.36458 5.73438 1.61719 5.64062 1.92969 5.64062H10.4297L13.2266 5.78906L12.8125 6.41406L10.2969 4.10938L8.64062 2.4375C8.55208 2.34896 8.47656 2.24479 8.41406 2.125C8.35677 2.00521 8.32812 1.86979 8.32812 1.71875C8.32812 1.44271 8.41927 1.21354 8.60156 1.03125C8.78906 0.848958 9.03125 0.757812 9.32812 0.757812C9.45312 0.757812 9.57552 0.786458 9.69531 0.84375C9.8151 0.895833 9.93229 0.973958 10.0469 1.07812L14.8594 5.89844C15.0729 6.10156 15.1797 6.34896 15.1797 6.64062Z"
                    fill="currentColor"
                  />
                </svg>
                <svg
                  v-if="showRedirectNotice"
                  viewBox="0 0 18 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9922 13.6211C17.9922 14.0664 17.8438 14.4414 17.5469 14.7461C17.2578 15.043 16.9102 15.1914 16.5039 15.1914C16.0898 15.1914 15.7422 15.043 15.4609 14.7461C15.1875 14.4414 15.0508 14.0742 15.0508 13.6445V9.4375L15.2148 5.66406L13.457 7.62109L3.48438 17.6055C3.14844 17.9336 2.77734 18.0977 2.37109 18.0977C2.09766 18.0977 1.84375 18.0234 1.60938 17.875C1.375 17.7266 1.18359 17.5391 1.03516 17.3125C0.894531 17.0781 0.824219 16.8281 0.824219 16.5625C0.824219 16.1562 0.992188 15.7812 1.32812 15.4375L11.3008 5.46484L13.2578 3.73047L9.35547 3.87109H5.27734C4.85547 3.87109 4.49219 3.73047 4.1875 3.44922C3.88281 3.16797 3.73047 2.82422 3.73047 2.41797C3.73047 2.01172 3.87891 1.66406 4.17578 1.375C4.47266 1.07813 4.84766 0.929688 5.30078 0.929688H16.3984C16.8828 0.929688 17.2695 1.07813 17.5586 1.375C17.8555 1.66406 18.0039 2.04688 18.0039 2.52344L17.9922 13.6211Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              <div
                class="close"
                @click="closeButton"
              >
                <svg
                  width="16"
                  height="15"
                  viewBox="0 0 16 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.958984 14.4004C0.854818 14.3092 0.786458 14.1986 0.753906 14.0684C0.721354 13.9382 0.721354 13.8079 0.753906 13.6777C0.792969 13.5475 0.861328 13.4368 0.958984 13.3457L6.94531 7.34961L0.958984 1.35352C0.861328 1.26237 0.796224 1.15169 0.763672 1.02148C0.73112 0.891276 0.73112 0.764323 0.763672 0.640625C0.796224 0.510417 0.861328 0.396484 0.958984 0.298828C1.05013 0.201172 1.16081 0.139323 1.29102 0.113281C1.42122 0.0807292 1.54818 0.0807292 1.67188 0.113281C1.79557 0.145833 1.90951 0.207682 2.01367 0.298828L8 6.29492L13.9961 0.298828C14.1393 0.155599 14.3151 0.0839844 14.5234 0.0839844C14.7318 0.0839844 14.9076 0.155599 15.0508 0.298828C15.2005 0.442057 15.2721 0.621094 15.2656 0.835938C15.2656 1.04427 15.194 1.2168 15.0508 1.35352L9.05469 7.34961L15.0508 13.3457C15.194 13.4824 15.2656 13.6549 15.2656 13.8633C15.2656 14.0716 15.194 14.2507 15.0508 14.4004C14.9076 14.5501 14.7318 14.6217 14.5234 14.6152C14.3151 14.6152 14.1393 14.5436 13.9961 14.4004L8 8.4043L2.01367 14.4004C1.90951 14.498 1.79557 14.5599 1.67188 14.5859C1.54818 14.6185 1.42122 14.6185 1.29102 14.5859C1.16081 14.5599 1.05013 14.498 0.958984 14.4004Z"
                    fill="black"
                  />
                </svg>
              </div>
            </div>
            <div
              class="recent-searches"
              :class="{
                active:
                  state.showRecentSearches &&
                  state.searchOption === 'email',
              }"
            >
              <div
                v-for="email in state.recentSearches.email"
                :key="email[i]"
                class="recent-search-item"
                @click="setRecentSearch(email)"
              >
                <div
                  class="recent-search-item__content"
                  @click="setRecentSearch(email)"
                >
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.1445 5.5625C13.3633 5.5625 13.5469 5.64062 13.6953 5.79688C13.8516 5.95312 13.9297 6.14062 13.9297 6.35938V13.8008L13.0391 11.0469L16.6484 15.8984C16.7969 16.1016 16.8633 16.3125 16.8477 16.5312C16.832 16.7422 16.7305 16.9102 16.543 17.0352C16.3398 17.1758 16.1289 17.2305 15.9102 17.1992C15.6992 17.168 15.5078 17.043 15.3359 16.8242L12.582 13.1797C12.4961 13.0625 12.4297 12.9531 12.3828 12.8516C12.3438 12.7422 12.3242 12.6289 12.3242 12.5117V6.35938C12.3242 6.14062 12.4023 5.95312 12.5586 5.79688C12.7148 5.64062 12.9102 5.5625 13.1445 5.5625ZM13.5664 23.2227C12.3086 23.2227 11.1133 23.0195 9.98047 22.6133C8.84766 22.207 7.80859 21.6406 6.86328 20.9141C5.91797 20.1953 5.09375 19.3594 4.39062 18.4062C4.22656 18.1875 4.16406 17.957 4.20312 17.7148C4.25 17.4648 4.375 17.2812 4.57812 17.1641C4.78125 17.0391 4.99219 17.0039 5.21094 17.0586C5.4375 17.1133 5.625 17.2461 5.77344 17.457C6.34375 18.2773 7.02734 18.9922 7.82422 19.6016C8.62891 20.2031 9.51562 20.6719 10.4844 21.0078C11.4531 21.3516 12.4805 21.5234 13.5664 21.5234C14.8867 21.5312 16.1211 21.2891 17.2695 20.7969C18.418 20.3047 19.4258 19.6211 20.293 18.7461C21.168 17.8711 21.8516 16.8594 22.3438 15.7109C22.8359 14.5547 23.082 13.3164 23.082 11.9961C23.082 10.6836 22.8359 9.45312 22.3438 8.30469C21.8516 7.14844 21.168 6.13672 20.293 5.26953C19.4258 4.39453 18.418 3.71484 17.2695 3.23047C16.1211 2.73828 14.8867 2.49219 13.5664 2.49219C12.2383 2.49219 11 2.73828 9.85156 3.23047C8.70312 3.71484 7.69141 4.39062 6.81641 5.25781C5.94922 6.125 5.26953 7.13672 4.77734 8.29297C4.28516 9.44141 4.03906 10.6719 4.03906 11.9844H2.33984C2.33984 10.4531 2.63281 9.01172 3.21875 7.66016C3.80469 6.30078 4.61328 5.10547 5.64453 4.07422C6.68359 3.04297 7.87891 2.23438 9.23047 1.64844C10.582 1.0625 12.0273 0.769531 13.5664 0.769531C15.0977 0.769531 16.5391 1.0625 17.8906 1.64844C19.25 2.23438 20.4453 3.04687 21.4766 4.08594C22.5078 5.11719 23.3164 6.3125 23.9023 7.67188C24.4883 9.02344 24.7812 10.4648 24.7812 11.9961C24.7812 13.5352 24.4883 14.9844 23.9023 16.3438C23.3164 17.6953 22.5078 18.8867 21.4766 19.918C20.4453 20.9492 19.25 21.7578 17.8906 22.3438C16.5391 22.9297 15.0977 23.2227 13.5664 23.2227ZM0.933594 9.89844H5.96094C6.20312 9.89844 6.38672 9.94922 6.51172 10.0508C6.63672 10.1523 6.69531 10.2852 6.6875 10.4492C6.6875 10.6133 6.62109 10.7891 6.48828 10.9766L4.02734 14.4688C3.86328 14.7109 3.67188 14.832 3.45312 14.832C3.23438 14.832 3.03516 14.7109 2.85547 14.4688L0.417969 10.9883C0.277344 10.793 0.203125 10.6133 0.195312 10.4492C0.195312 10.2852 0.257812 10.1523 0.382812 10.0508C0.507812 9.94922 0.691406 9.89844 0.933594 9.89844Z"
                      fill="#8C8E91"
                    />
                  </svg>
                  <div class="recent-search-contact">
                    {{ email }}
                  </div>
                </div>
                <div
                  class="recent-search-item__remove"
                  @click="removeRecentSearch(email)"
                >
                  <svg
                    width="23"
                    height="24"
                    viewBox="0 0 23 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.5 23.2227C9.96094 23.2227 8.51562 22.9297 7.16406 22.3438C5.8125 21.7578 4.62109 20.9492 3.58984 19.918C2.55859 18.8867 1.74609 17.6953 1.15234 16.3438C0.566406 14.9844 0.273438 13.5352 0.273438 11.9961C0.273438 10.4648 0.566406 9.02344 1.15234 7.67188C1.73828 6.3125 2.54688 5.11719 3.57812 4.08594C4.61719 3.04687 5.8125 2.23438 7.16406 1.64844C8.51562 1.0625 9.95703 0.769531 11.4883 0.769531C13.0273 0.769531 14.4727 1.0625 15.8242 1.64844C17.1758 2.23438 18.3672 3.04687 19.3984 4.08594C20.4375 5.11719 21.25 6.3125 21.8359 7.67188C22.4297 9.02344 22.7266 10.4648 22.7266 11.9961C22.7266 13.5352 22.4336 14.9844 21.8477 16.3438C21.2617 17.6953 20.4492 18.8867 19.4102 19.918C18.3789 20.9492 17.1875 21.7578 15.8359 22.3438C14.4844 22.9297 13.0391 23.2227 11.5 23.2227ZM7.77344 16.5781C8.01562 16.5781 8.21875 16.4961 8.38281 16.332L11.5 13.2031L14.6289 16.332C14.7852 16.4961 14.9844 16.5781 15.2266 16.5781C15.4609 16.5781 15.6602 16.4961 15.8242 16.332C15.9883 16.168 16.0703 15.9688 16.0703 15.7344C16.0703 15.4922 15.9883 15.293 15.8242 15.1367L12.6836 12.0078L15.8359 8.87891C16 8.71484 16.082 8.51172 16.082 8.26953C16.082 8.04297 16 7.85156 15.8359 7.69531C15.6719 7.53125 15.4766 7.44922 15.25 7.44922C15.0312 7.44922 14.8359 7.53516 14.6641 7.70703L11.5 10.8477L8.35938 7.71875C8.1875 7.54688 7.99219 7.46094 7.77344 7.46094C7.54688 7.46094 7.35156 7.54297 7.1875 7.70703C7.02344 7.86328 6.94141 8.05859 6.94141 8.29297C6.94141 8.51953 7.02344 8.71875 7.1875 8.89062L10.3281 12.0078L7.1875 15.1484C7.02344 15.3125 6.94141 15.5078 6.94141 15.7344C6.94141 15.9688 7.02344 16.168 7.1875 16.332C7.35156 16.4961 7.54688 16.5781 7.77344 16.5781Z"
                      fill="#5E6165"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <!-- recent searches dropdown works outside of search input container -->
        </div>

        <h3
          v-if="!state.searchActive && state.searchOption !== 'phone'"
          class="redirect-notice consent-notice"
        >
          <span :class="{'white-text' : main.breachCount === 0 }">By providing your email address for a breach scan, you consent to receive emails with your scan and product updates from </span>
          <a
            :href="`https://www.cloaked.com/?${getUTMParams()}`"
            target="_blank"
            class="external"
            @click="fbqTrackingLead"
          >
            <span>Cloaked</span>

            <span>
              <svg
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.9922 13.6211C17.9922 14.0664 17.8438 14.4414 17.5469 14.7461C17.2578 15.043 16.9102 15.1914 16.5039 15.1914C16.0898 15.1914 15.7422 15.043 15.4609 14.7461C15.1875 14.4414 15.0508 14.0742 15.0508 13.6445V9.4375L15.2148 5.66406L13.457 7.62109L3.48438 17.6055C3.14844 17.9336 2.77734 18.0977 2.37109 18.0977C2.09766 18.0977 1.84375 18.0234 1.60938 17.875C1.375 17.7266 1.18359 17.5391 1.03516 17.3125C0.894531 17.0781 0.824219 16.8281 0.824219 16.5625C0.824219 16.1562 0.992188 15.7812 1.32812 15.4375L11.3008 5.46484L13.2578 3.73047L9.35547 3.87109H5.27734C4.85547 3.87109 4.49219 3.73047 4.1875 3.44922C3.88281 3.16797 3.73047 2.82422 3.73047 2.41797C3.73047 2.01172 3.87891 1.66406 4.17578 1.375C4.47266 1.07813 4.84766 0.929688 5.30078 0.929688H16.3984C16.8828 0.929688 17.2695 1.07813 17.5586 1.375C17.8555 1.66406 18.0039 2.04688 18.0039 2.52344L17.9922 13.6211Z"
                  fill="#FF550C"
                />
              </svg>
            </span>
            <div class="underline">
              <div class="inner" />
            </div>
          </a>
        </h3>
        
        <h3
          v-if="showRedirectNotice"
          class="redirect-notice"
          :class="{'white-text' : main.breachCount === 0}"
        >
          <span> Number Scan is Powered by </span>
          <a
            :href="`https://www.cloaked.com/?${getUTMParams()}`"
            target="_blank"
            class="external"
            @click="fbqTrackingLead"
          >
            <span>Cloaked</span>

            <span>
              <svg
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.9922 13.6211C17.9922 14.0664 17.8438 14.4414 17.5469 14.7461C17.2578 15.043 16.9102 15.1914 16.5039 15.1914C16.0898 15.1914 15.7422 15.043 15.4609 14.7461C15.1875 14.4414 15.0508 14.0742 15.0508 13.6445V9.4375L15.2148 5.66406L13.457 7.62109L3.48438 17.6055C3.14844 17.9336 2.77734 18.0977 2.37109 18.0977C2.09766 18.0977 1.84375 18.0234 1.60938 17.875C1.375 17.7266 1.18359 17.5391 1.03516 17.3125C0.894531 17.0781 0.824219 16.8281 0.824219 16.5625C0.824219 16.1562 0.992188 15.7812 1.32812 15.4375L11.3008 5.46484L13.2578 3.73047L9.35547 3.87109H5.27734C4.85547 3.87109 4.49219 3.73047 4.1875 3.44922C3.88281 3.16797 3.73047 2.82422 3.73047 2.41797C3.73047 2.01172 3.87891 1.66406 4.17578 1.375C4.47266 1.07813 4.84766 0.929688 5.30078 0.929688H16.3984C16.8828 0.929688 17.2695 1.07813 17.5586 1.375C17.8555 1.66406 18.0039 2.04688 18.0039 2.52344L17.9922 13.6211Z"
                  fill="#FF550C"
                />
              </svg>
            </span>
            <div class="underline">
              <div class="inner" />
            </div>
          </a>
        </h3>
      </div>
    </div>
    <div class="phew-content">
      <div class="content-inner">
        <div class="content-left">
          <h1>
            Good news, your {{ goodNewsBanner() }} hasn't been
            breached yet.
          </h1>
          <div class="breach-status-cta">
            <div class="breach-status-cta-content">
              <h2>What can you do now?</h2>
              <ul>
                <li>
                  <div class="order-dot">
                    1
                  </div>
                  <p>Set up fresh passwords for all accounts</p>
                </li>
                <li>
                  <div class="order-dot">
                    2
                  </div>
                  <p>Set up MFA for all accounts</p>
                </li>
                <li>
                  <div class="order-dot">
                    3
                  </div>
                  <p>Stop using this email address to sign up for new accounts</p>
                </li>
                <li>
                  <div class="order-dot">
                    4
                  </div>
                  <p>
                    Scan your phone number for exposure
                    <iframe
                      :src="`https://number-scan-widget.cloaked.com/?widget=input&${getUTMParams()}`"
                      title="Phone Input Widget"
                      class="phone-input-iframe"
                      style="min-width: 320px; width: 100%; max-width: 400px; height: 80px; border: none; margin-top: 16px; border-radius: 8px; overflow: hidden;"
                    />
                  </p>
                </li>
              </ul>
            </div>
          </div>
          <div class="built-and-trusted">
            <builtAndTrusted />
          </div>
          <p>
            Have I been breached is Built by Cloaked, an all-in-one
            solution for protecting your privacy online.
            <a
              :href="`https://cloaked.com/?${getUTMParams()}`"
              target="_blank"
              @click="fbqTrackingLead"
            >Try out Cloaked with 30-day money back guarantee</a>
          </p>
        </div>
        <div class="content-right">
          <cardAlert
            :cta="true"
            :accounts="main.breachCount"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
@use "sass:color";
header {
  padding: 30px 0px 0px 0px;
  overflow: hidden;
  background-color: white;
  z-index: 111111;

  // testing styles for search wrapper concept

  .search {
    border: 3px solid #191e23;
    border-radius: 70px;
    padding: 12px 24px 12px 24px;
    @media screen and (max-width: 1023px) {
      border-radius: 30px !important;
    }
    &.error {
      border: 3px solid #f24141 !important;
    }
  }

  .active h1.text-content{
    color: white !important;
  }

  .search-inner {
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .recent-searches {
    width: 100%;
    z-index: 999;
    visibility: hidden;
    opacity: 0;
    height: 0;
    transition: all 0.25s ease;
    &.active {
      visibility: visible;
      opacity: 1;
      padding: 20px;
      height: auto;
    }
    
    .recent-search-item {
      color: #000;
      padding: 20px 16px 20px 8px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      transition: all 0.25s ease;
      cursor: pointer;
      visibility: hidden;
      opacity: 0;
      animation: 0.75s forwards search-item-animation;
      @keyframes search-item-animation {
        0% {
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
        }
        100% {
          opacity: 1;
          visibility: visible;
          transform: translateY(0px);
        }
      }
      &:hover {
        background: #f7f7f7;
      }
      &__content {
        display: flex;
        align-items: center;
        flex: 1;
        svg {
          margin-right: 10px;
        }
        @media screen and (max-width: 1023px) {
          svg {
            width: 18px !important;
          }
        }
      }
      &__remove {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }
    }
  }

  .white-text{
    color:white;
  }

  .redirect-notice {
    text-align: center;
    margin-top: 24px;
    margin-bottom: -35px;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    a {
      color: #ff550c;
      width: fit-content;
      position: relative;
      font-weight: 600;
      display: inline-flex;
      flex-wrap: nowrap;

      svg {
        margin-left: 4px;
        width: 12px;
      }
      .underline {
        position: absolute;
        right: 0;
        height: 2px;
        bottom: 0px;
        width: 100%;
        transition: all 0.25s ease-in-out;
        .inner {
          width: 0%;
          height: 2px;
          background-color: #ff550c;
          transition: all 0.25s ease-in-out;
        }
      }
      &:hover {
        .underline {
          .inner {
            width: 100%;
            background-color: #ff550c;
          }
        }
      }
    }

    &.consent-notice {
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;

      a { 
        font-size: 600;
      }
    }

    @media screen and (max-width: 1024px) {
      margin-bottom: 0;
    }
  }

  // v2 removal

  @media (min-width: 1024px) {
    padding: 60px 32px 65px 32px;
    overflow: hidden;
    max-height: 1000px;
    transition: all 0.3s ease;

    &.search {
      padding-bottom: 20px;
      max-height: 180px;
      position: relative;

      .logo {
        .name {
          left: 120px;
          transform: translateX(0);

          span {
            font-size: 16px;
            line-height: 15px;
          }

          sup {
            right: -9px;
            font-size: 10px;
          }

          &:hover {
            cursor: pointer;
          }
        }
      }

      .content {
        margin-top: 0px;
        padding-bottom: 0px;
      }
    }
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    position: relative;
    padding: 0 20px;
  }

  .logo {
    display: flex;
    align-items: center;

    .icon {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      position: relative;
      z-index: 2;

      svg {
        color: #303539;
        transition: color 0.3s ease;
        width: 100%;
        height: auto;

        body.danger & {
          color: #ff4d4d;
        }

        body.alert & {
          color: #ffc700;
        }

        body.nice & {
          color: #1a9fcc;
        }

        body.good & {
          color: #c471ed;
        }
      }

      @media (min-width: 1024px) {
        width: 80px;
        height: 80px;
      }

      &:hover {
        cursor: pointer;
      }

      span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: #fff;
        transition: all 0.3s ease;
        font-weight: 900;
        font-size: 30px;
        line-height: 29px;
        letter-spacing: 0px;
        display: block;
        width: 100%;
        text-align: center;

        @media (min-width: 1024px) {
          font-size: 40px;
          line-height: 38px;
        }

        &:before {
          content: '?';

          body.danger &,
          body.alert &,
          body.nice & {
            content: '!';
          }

          body.good & {
            content: '🎉';
          }
        }
      }
    }

    .name {
      position: absolute;
      top: -99999px;
      left: -99999px;
      opacity: 0;
      visibility: hidden;
      cursor: pointer;
      .underline {
        height: 2px;
        width: 0%;
        background-color: #000c;
        transition: all 0.25s ease-in-out;
      }
      &:hover {
        .underline {
          width: 100%;
        }
      }

      @media (min-width: 1024px) {
        top: 27px;
        left: 50%;
        transform: translateX(-50%);
        opacity: 1;
        visibility: visible;
        transition: all 0.6s ease;
      }

      span {
        font-size: 24px;
        font-weight: 600;
        line-height: 22px;
        color: #191e23;
        transition: all 0.3s ease;
      }

      sup {
        position: absolute;
        right: -11px;
        top: 0;
        font-size: 12px;
      }
    }
  }

  .menu-button {
    position: absolute;
    top: 28px;
    right: 28px;
    width: fit-content;

    @media (min-width: 1024px) {
      top: -99999px;
      left: -99999px;
      right: auto;
      opacity: 0;
      visibility: hidden;
    }

    .menu-button-icon {
      padding-left: 15px;
    }
    .underline {
      height: 2px !important;
      width: 0% !important;
      background-color: #000 !important;
      transition: all 0.25s ease-in-out !important;
      margin-top: 4px;
    }
    &:hover {
      .underline {
        width: 100% !important;
      }
    }

    span {
      font-family: Work Sans;
      font-size: 16px;
      font-weight: 600;
      line-height: 15px;
      letter-spacing: 0px;
      text-align: left;
      color: #191e23;
      display: inline-block;
      position: relative;
    }
  }


  @media screen and (max-width: 1023px) {
    .menu-button {
      top: 12px;
    }
  }

  nav {
    overflow: hidden;
    max-height: 5px;
    position: relative;
    margin-top: 10px;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    display: flex;
    justify-content: flex-start;
    max-height: unset;
    height: auto;
    margin-top: 0;
    overflow: visible;
    position: absolute;
    top: 30px;
    right: 0;
    //new edit
    right: 20px;

    @media (min-width: 1024px) {
      max-height: unset;
      height: auto;
      margin-top: 0;
      overflow: visible;
      position: absolute;
      top: 30px;
      right: 0;
    }

    &.active {
      height: auto;
      max-height: 335px;
      transition: max-height 1.5s cubic-bezier(0.165, 0.84, 0.44, 1);
      transition-delay: 0.4s;
      margin-bottom: 20px;
      display: flex;
      justify-content: flex-end;

      @media (min-width: 1024px) {
        margin-bottom: 0;
      }

      ul {
        &:after {
          width: 100%;
        }

        li {
          opacity: 1;
          transform: translateX(0px);

          &:nth-of-type(1) {
            transition-delay: 0.2s;
          }

          &:nth-of-type(2) {
            transition-delay: 0.4s;
          }
        }
      }
    }

    ul {
      padding-top: 28px;
      padding-bottom: 36px;
      display: none;

      @media (min-width: 1024px) {
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
      }

      &:after {
        content: '';
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        height: 5px;
        width: 0;
        background-color: #191e23;
        transition: all 0.6s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        border-radius: 1px;

        @media (min-width: 1024px) {
          display: none;
        }
      }

      li {
        font-family: Work Sans;
        font-size: 30px;
        font-weight: 600;
        line-height: 34px;
        letter-spacing: -1.5px;
        text-align: right;
        color: #191e23;
        margin-top: 40px;
        display: block;
        transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        opacity: 0;
        transform: translateX(-30px);

        @media (min-width: 1024px) {
          opacity: 1;
          transform: translateX(0px);
          margin-top: 0;
          margin-left: 31px;
          font-family: Work Sans;
          font-size: 16px;
          line-height: 15px;
          letter-spacing: 0px;
        }

        a {
          position: relative;
          display: inline-flex;
          align-items: center;

          svg {
            width: 14px;
            height: auto;
            display: inline-block;
            margin-left: 9px;
          }

          &:after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 50%;
            transform: translateX(-50%);
            height: 3px;
            width: 0%;
            background-color: #191e23;
            transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
            transition-delay: 0.2s;
          }
        }

        &:first-of-type {
          margin-top: 0;

          @media (min-width: 1024px) {
            margin-left: 0;
          }
        }

        &.active {
          a {
            &:after {
              width: 100%;
            }
          }
        }

        &:hover {
          cursor: pointer;
        }
      }
    }
  }

  .content {
    max-width: 1117px;
    margin: 0 auto 0 auto;
    width: 100%;
    position: relative;
    transition: all 0.4s ease;

    @media (min-width: 1024px) {
      margin-top: 70px;
      body.danger &,
      body.alert &,
      body.nice &,
      body.good & {
        margin-top: 0;
      }
    }

    .text {
      opacity: 0;
      transition: all 0.2s ease;
      position: absolute;
      transform: translateY(-20px);
      visibility: hidden;
      max-height: 110px;
      overflow: hidden;
      margin-top: 0px;

      &.active {
        opacity: 1;
        position: relative;
        transform: translateY(0px);
        visibility: visible;
        max-height: 600px;
        margin-top: 20px;
        //edit
        margin-bottom: 42px;
      }

      h1 {
        color: #191e23;
        margin-bottom: 8px;
        font-weight: 600;
        letter-spacing: -1.5px;

        @media (min-width: 820px) {
          font-size: 60px;
          font-weight: 600;
          line-height: 57px;
          letter-spacing: -2px;
          margin-bottom: 25px;
        }
        @media (max-width: 819px) {
          font-size: 46px;
          margin-bottom: 22px;
        }

        b {
          color: #f24141;
        }
      }

      p {
        color: #191e23;
        margin-top: 20px;
        font-size: 18px;
        font-weight: 400;
        line-height: 26px;
        letter-spacing: -1px;

        @media (min-width: 820px) {
          font-size: 24px;
          line-height: 35px;
        }

        &:first-of-type {
          margin-top: 0;
        }
      }
    }
    // v2 here
    .search {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      margin-top: 45px;
      transition: all 0.4s ease;

      @media (min-width: 1024px) {
        bottom: 0;
        left: 50%;
        transform: scale(1) translateX(-50%);
        transform-origin: left;
        max-width: 1117px;
        width: 100%;
        margin-top: 0;
      }

      &.submit {
        @media (min-width: 1024px) {
          .input {
            width: calc(100% - 80px);
          }

          .buttons {
            right: 118px;
          }
        }

        .submit {
          opacity: 1;
          visibility: visible;
        }
      }

      .submit {
        position: absolute;
        right: 10px;
        bottom: -9px;
        z-index: 3;
        width: 40px;
        height: 40px;
        background-color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;

        svg {
          width: 50%;
          height: auto;
        }

        &:hover {
          cursor: pointer;
        }

        &.submitSmall {
          svg {
            width: 37%;
          }
        }

        @media (min-width: 1024px) {
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          bottom: auto;
          width: 70px;
          height: 70px;
          z-index: 1;
        }
      }

      &.active {
        margin-top: 0;

        @media (min-width: 1024px) {
          max-width: 600px;
          transform: scale(0.6) translateX(-50%);
          bottom: 0;
        }

        .country {
          opacity: 0;
          visibility: hidden;
          transform: translateY(30px);
        }

        .input {
          svg {
            left: 21px;
          }

          input {
            border-width: 2px;
            padding: 16px 24px 16px 24px !important;
            text-align: center;
            &:disabled {
              background-color: transparent;
            }

            @media (min-width: 1024px) {
              padding: 24px 24px 24px 24px !important;
            }
          }

          .error {
            display: none;
          }
        }

        .buttons {
          display: none;

          @media (min-width: 1024px) {
            display: block;
            opacity: 0;
            transform: translateY(30px);
            visibility: hidden;
          }
        }

        .close {
          opacity: 1;
          visibility: visible;
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 0%;
          justify-content: flex-end;
        }
      }

      .close {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        opacity: 0;
        visibility: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3;

        &:hover {
          cursor: pointer;
        }

        @media (min-width: 1024px) {
          width: 60px;
          height: 60px;
        }

        svg {
          width: 14px;
          height: auto;

          @media (min-width: 1024px) {
            width: 20px;
          }
        }
      }

      .input {
        order: 2;
        width: 100%;
        position: relative;
        z-index: 2;
        transition: all 0.4s ease;

        &.error {
          input {
            border-color: #f24141;
          }
        }

        &.phone {
          input {
            padding-left: 40px;

            @media (min-width: 1024px) {
              padding-left: 112px;
            }
          }
        }

        .error {
          position: absolute;
          bottom: 50px;
          left: 26px;

          @media (min-width: 1024px) {
            bottom: 70px;
            left: 86px;
          }

          p {
            color: #f24141;
            font-size: 14px;
            font-weight: 400;
            line-height: 18px;
            letter-spacing: -0.5px;
          }
        }

        .country {
          position: absolute;
          top: 50%;
          left: 15px;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          background-color: #fff;

          @media (min-width: 1024px) {
            left: 12px;
          }

          .flag {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background-color: #191e23;

            @media (min-width: 1024px) {
              width: 48px;
              height: 48px;
            }
          }

          .code {
            margin-left: 7px;

            span {
              display: inline-block;
              font-size: 16px;
              font-weight: 500;
              line-height: 21px;
              letter-spacing: -1px;
              color: #191e23;

              @media (min-width: 1024px) {
                font-size: 24px;
                line-height: 31px;
              }
            }
          }
        }

        svg {
          position: absolute;
          top: 50%;
          left: 26px;
          transform: translateY(-50%);
          width: 16px;
          height: auto;

          @media (min-width: 1024px) {
            width: 28px;
          }
        }

        input {
          width: 100%;
          border-radius: 119.692px;
          border: none;
          font-size: 16px;
          font-weight: 500;
          line-height: 21px;
          letter-spacing: -1px;
          color: #191e23;
          outline: none;
          transition: all 0.4s ease;

          @media (min-width: 1024px) {
            border-width: 10px;
            padding: 30px 24px 30px 85px;
            font-size: 24px;
            line-height: 31px;
          }
        }
      }

      .buttons {
        display: flex;
        gap: 8px;
        width: 100%;
        order: 1;
        margin-bottom: 14px;

        @media (min-width: 1024px) {
          position: absolute;
          top: 50%;
          right: 36px;
          transform: translateY(-50%);
          width: auto;
          gap: 13px;
          z-index: 3;
          transition: all 0.4s ease;
        }

        button {
          padding: 15px 10px;
          border-radius: 84px;
          border: 2px solid #191e23;
          color: #191e23;
          background-color: transparent;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 14px;
          font-style: normal;
          font-weight: 600;
          line-height: 95%;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            display: inline-block;
            margin-right: 7px;
            max-width: 26px;
            max-height: 19px;
            height: auto;
            width: auto;
          }

          &.active {
            background-color: #191e23;
            color: #fff;

            &:hover {
              background-color: #191e23;
            }
          }

          &:hover {
            cursor: pointer;
            background-color: color.adjust(#191e23, $lightness: 80%);
          }

          @media (min-width: 1024px) {
            width: auto;
            padding: 18px 23px;
            font-size: 18px;
          }
        }
      }
    }
  }
}

nav {
  .underline {
    height: 2px !important;
    width: 0% !important;
    background-color: #000 !important;
    transition: all 0.25s ease-in-out !important;
    margin-top: 4px;
  }
  &:hover {
    .underline {
      width: 100% !important;
    }
  }
}
.resultsview {
  @media screen and (max-width: 1023px) {
    padding-top: 38px !important;
    display: none;

    & + h3.redirect-notice {
      display: none;
    }
  }
  .search {
    margin-top: 42px !important;
    &.active {
      @media (min-width: 1024px) {
        transform: scale(0.6) translateX(-50%) translateY(-160px) !important;
        margin-top: 0px !important;
      }
    }
  }
}
.phew-content {
  display: none;
  .content-inner {
    margin-top: 24px;
    display: flex;
    justify-content: center;
    align-items: start;
    max-width: 1117px;
    margin: 0 auto;
    padding: 80px 20px 0px 20px;
  }
}

.phew {
  background-color: #000;
  max-height: fit-content !important;
  background-image: url('~@/assets/images/confetti.png');
  background-position: top right;
  background-repeat: no-repeat;
  background-size: contain;
  .menu-button {
    color: #fff !important;
    span {
      color: #fff;
      &:before {
        background-color: #fff;
      }
      &:after {
        background-color: #fff;
      }
    }
  }
  .recent-search-contact {
    color: #5e6165 !important;
  }
  .name {
    .underline {
      height: 2px;
      width: 0%;
      background-color: #fff !important;
      transition: all 0.25s ease-in-out;
    }
    &:hover {
      .underline {
        width: 100%;
      }
    }
  }
  nav {
    .underline {
      height: 2px !important;
      width: 0% !important;
      background-color: #fff !important;
      transition: all 0.25s ease-in-out !important;
      margin-top: 4px;
    }
    &:hover {
      .underline {
        width: 100% !important;
      }
    }
  }

  .country {
    background-color: #000 !important;
    color: #fff !important;
    .code {
      color: #fff !important;
      span {
        color: #fff !important;
      }
    }
  }

  .name {
    color: #fff;
    span {
      color: #fff !important;
    }
  }
  nav {
    color: #fff !important;
    a {
      color: #fff !important;
      svg {
        fill: #fff !important;
      }
    }
  }
  .search {
    border: 4px solid #fff;
    background-color: #000;
    input {
      background-color: #000 !important;
      color: #fff !important;
    }
    .input {
      color: #fff !important;
      svg {
        path {
          stroke: #fff !important;
        }
        ellipse {
          stroke: #fff !important;
        }
      }
    }
  }
  .buttons {
    button {
      border: 2px solid #fff !important;
      color: #fff !important;
      &:hover {
        background-color: #fff !important;
        color: #000 !important;
      }
    }
    .active {
      background-color: #fff !important;
      color: #000 !important;
    }
  }
  .buttons-mobile {
    button {
      border: 2px solid #fff !important;
      color: #fff !important;
      &:hover {
        background-color: #fff !important;
        color: #000 !important;
      }
    }
    .active {
      background-color: #fff !important;
      color: #000 !important;
    }
  }
  .close {
    svg {
      path {
        stroke: #fff !important;
        fill: #fff !important;
      }
    }
  }
  .phew-content {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;

    .content-left {
      margin-right: 40px;
    }
    h1 {
      font-size: 48px;
      font-style: normal;
      font-weight: 600;
      color: #fff;
      @media screen and (max-width: 900px) {
        font-size: 30px;
        font-style: normal;
        font-weight: 600;
        font-size: 30px;
      }
    }
    .built-and-trusted {
      margin-top: 32px;
    }
    p {
      margin-top: 32px;
      color: rgba(255, 255, 255, 0.7);
      max-width: 80%;
      margin-bottom: 32px;
      a {
        color: rgba(255, 255, 255, 0.7);
        text-decoration: underline;
        font-weight: normal;
        transition: all 0.25s ease;
        &:hover {
          color: #fff;
        }
      }
    }
  }
}

@media screen and (max-width: 1023px) {
  /* Your styles go here */
  .buttons-mobile {
    display: flex !important;
  }
  .buttons {
    display: none !important;
  }
  .input {
    svg {
      left: 0px !important;
    }
    input {
      width: calc(100% - 40px) !important;
      margin-left: 24px !important;
    }
    .country {
      left: 0px !important;
    }
  }
  .recent-searches {
    padding: 0px !important;
    .recent-search-item {
      padding: 20px 8px 20px 0px !important;
    }
  }
  .phew {
    background-position: bottom -100px left -50px !important;
    .phew-content {
      .built-and-trusted,
      p {
        display: none;
      }
      .content-inner {
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
      }

      .content-left {
        margin-left: 20px;
        margin-top: 60px;
      }
      .content-right {
        padding-bottom: 20px;
        width: 100%;
        // margin-top: 40px !important;
        .card-container {
          margin-top: 40px;
        }
      }
    }
  }
  .icon {
    width: 48px !important;
    height: 48px !important;
  }
}

@media screen and (max-width: 1024px) {
  .phew {
    .phew-content {
      .content-left {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .phew {
    background-position: bottom -223px right 20% !important;
    background-size: auto !important;

    .phew-content {
      .content-left {
        margin-top: 0px;
      }
      .content-right {
        .card-container {
          margin-top: 20px;
        }
      }
    }
  }
}

@media screen and (max-width: 380px) {
  .phew {
    .phew-content {
      .content-right {
        .card-container {
          margin-top: 0;
        }
      }
    }
  }
}

.buttons-mobile {
  display: none;
  gap: 16px;
  width: 100%;
  margin-bottom: 32px;
  button {
    padding: 15px 10px;
    border-radius: 84px;
    border: 2px solid #191e23;
    color: #191e23;
    background-color: transparent;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 95%;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      display: inline-block;
      margin-right: 7px;
      max-width: 26px;
      max-height: 19px;
      height: auto;
      width: auto;
    }

    &.active {
      background-color: #191e23;
      color: #fff;

      &:hover {
        background-color: #191e23;
      }
    }

    &:hover {
      cursor: pointer;
      background-color: color.adjust(#191e23, $lightness: 80%);
    }
  }
}

.search {
  input {
    max-width: 60% !important;
    @media screen and (max-width: 1023px) {
      max-width: 90% !important;
    }
  }
  &.active {
    input {
      max-width: 100% !important;
    }
  }
}

.breach-status-cta-content {
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #1c1c1c;
  padding: 24px;
  color: #f1f1f1;
  margin-top: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.6);

  h2 {
    font-size: 32px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  ul {
    margin-top: 16px;
    padding-left: 0;

    li {
      list-style: none;
      font-size: 16px;
      font-weight: 500;
      color: #f7f7f7;
      margin-bottom: 20px;
      display: flex;
      align-items: flex-start;

      .order-dot {
        font-size: 16px;
        font-weight: 500;
        color: #000;
        margin-right: 16px;
        background-color: #f1f1f1;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        flex-shrink: 0;
      }

      p {
        margin-top: 4px !important;
        line-height: 1.5;
        flex: 1;
        margin-bottom: 0 !important;
        color: white;
        display: block !important;
      }

      iframe {
        display: block;
      }
    }
  }

  .breach-status-cta-button {
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    display: flex;
    width: fit-content;
    padding: 3px;
    border-radius: 99px;
    background-image: linear-gradient(
      47deg,
      transparent 10%,
      transparent 36%,
      transparent 92%
    );
    cursor: pointer;
    text-decoration: none;
    transition: transform 0.3s;

    .block {
      background-color: #000;
      border-radius: 99px;
      padding: 12px 18px;
      display: flex;
      align-items: center;
      transition: all 0.25s ease;
    }

    &:hover {
      background-image: linear-gradient(
        47deg,
        #ff7a00 10%,
        #ff4949 36%,
        #8dbbff 92%
      );

      .block {
        background-color: #fff;
        color: #000;
      }
    }
  }
}

.phone-input-iframe {
    min-width: 320px;
    width: 400px;
    height: 80px;
    border: 0px;
    margin-top:20px;
    display: block;
}

@media screen and (max-width: 575px) {
  iframe.phone-input-iframe {
    min-width: auto !important;
    width: calc(100% + 48px) !important;
    margin-left: -48px;
  }
}
.text-dark h1 {
  color:black !important;
}
</style>
